import { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { DragScrollWrapper } from '@/components/common/etc'
import { RootCategoryTab, SubCategoryTab } from '@/components/common/navigation/tab'
import { BrandDetailListIdEnum, TabTitleEnum } from '@/constants/category-navigation-tab.enum'
import { LAYOUT_UNITS, Z_INDEXES } from '@/constants/legacy/constLayout'
import { HeaderIdEnum } from '@/constants/local-navigation-tab-title.enum'
import { LocationEnum } from '@/constants/location.enum'
import useBrandEventTracker from '@/containers/event-tracker/brand.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import { useBrandCategory } from '@/containers/hooks/brand/useBrandCategory'
import { BrandNameQuery } from '@/graphql/generated/operations'
import { round } from '@/utils/utilCommon'

type QueryCategoryType = {
  rootCategoryId: string
  subCategoryId: string
}

type VisibleGradientType = {
  leftGradient: boolean
  rightGradient: boolean
}

const leftMost = 0

interface CategoryNavigationProps {
  brandInfo?: BrandNameQuery
}

const CategoryNavigation = ({ brandInfo }: CategoryNavigationProps) => {
  const { query } = useCustomRouter()
  const { rootCategoryId, subCategoryId } = query as QueryCategoryType
  const { categoryData, selectedCategoryName, selectedSubCategoryName } = useBrandCategory()

  const categoryTabListRef = useRef<HTMLDivElement | null>(null)
  const lastScrollRef = useRef<number>(0)
  const rootCategoryTabHeight = categoryTabListRef.current?.offsetHeight || 0

  const [isFixedTabList, setIsFixedTabList] = useState<boolean>(false)
  const [visibleRootGradient, setVisibleRootGradient] = useState<VisibleGradientType>({
    leftGradient: false,
    rightGradient: false,
  })
  const [visibleSubGradient, setVisibleSubGradient] = useState<VisibleGradientType>({
    leftGradient: false,
    rightGradient: false,
  })
  const [isScrollDown, setIsScrollDown] = useState<boolean>(false)
  const { trackViewBrandListEvent } = useBrandEventTracker()

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [categoryTabListRef.current, query])

  const onScroll = () => {
    if (!categoryTabListRef) {
      return
    }
    const { pageYOffset } = window
    const isScrollDown = lastScrollRef.current <= pageYOffset
    // 스크롤 방향에 따라 고정 서브 카테고리 탭 노출 분기
    setIsScrollDown(isScrollDown)

    const headerOffset = document.getElementById(HeaderIdEnum.Header)?.offsetHeight || 0
    const listOffsetTop = document.getElementById(BrandDetailListIdEnum.BrandDetailList)?.offsetTop || 0

    // 루트 카테고리 하단에 고정된 서브 카테고리 탭 노출하도록 상태값 변경
    setIsFixedTabList(!isScrollDown && listOffsetTop <= pageYOffset + headerOffset + rootCategoryTabHeight)
    lastScrollRef.current = pageYOffset
  }

  const displayRootCategoryGradient = (containerRef: HTMLDivElement | null) => {
    if (!containerRef) {
      return
    }
    const { scrollLeft, scrollWidth } = containerRef
    const { width } = containerRef?.getBoundingClientRect()
    setVisibleRootGradient({
      leftGradient: scrollLeft !== leftMost,
      rightGradient: round(scrollLeft) < round(scrollWidth - width),
    })
  }

  const displaySubCategoryGradient = (containerRef: HTMLDivElement | null) => {
    if (!containerRef) {
      return
    }
    const { scrollLeft, scrollWidth } = containerRef
    const { width } = containerRef?.getBoundingClientRect()
    setVisibleSubGradient({
      leftGradient: scrollLeft !== leftMost,
      rightGradient: round(scrollLeft) < round(scrollWidth - width),
    })
  }

  // categories 안에 현재 보고있는 rootCategory에 하위 subCategory 가공하여 사용
  const subCategories = categoryData?.find((rootCategory) => rootCategory.gid === rootCategoryId)?.children || []

  useEffect(() => {
    trackViewBrandListEvent({
      brandName: brandInfo?.brand?.name as string,
      location: query?.location as LocationEnum,
      selectedCategoryName,
      selectedSubCategoryName,
    })
  }, [brandInfo?.brand?.name, rootCategoryId, subCategoryId])

  return (
    <>
      <StyledRootCategory ref={categoryTabListRef}>
        <DragScrollWrapper elementScroll={displayRootCategoryGradient}>
          <StyledRootCategoryTabList>
            <RootCategoryTab isActive={!rootCategoryId} tabTitle={TabTitleEnum.CategoryAll} />
            {categoryData?.map((rootCategory) => (
              <RootCategoryTab
                key={rootCategory.gid}
                tabTitle={rootCategory.name}
                categoryId={rootCategory.gid ?? ''}
                isActive={rootCategoryId === rootCategory.gid}
              />
            ))}
            {visibleRootGradient.leftGradient && <StyledTabLeftGradient />}
            {visibleRootGradient.rightGradient && <StyledTabRightGradient />}
          </StyledRootCategoryTabList>
        </DragScrollWrapper>
      </StyledRootCategory>
      {rootCategoryId && (
        <>
          <StyledSubCategoryTabList>
            <SubCategoryTab tabTitle={TabTitleEnum.CategoryAll} isActive={!subCategoryId} />
            {subCategories.map((subCategory) => (
              <SubCategoryTab
                key={subCategory.gid}
                tabTitle={subCategory.name}
                categoryId={subCategory.gid ?? ''}
                isActive={subCategoryId === subCategory.gid}
              />
            ))}
          </StyledSubCategoryTabList>
          <StyledDivider />
          <StyledFixedSubCategory isFixedTabList={isFixedTabList} isScrollDown={isScrollDown}>
            <DragScrollWrapper elementScroll={displaySubCategoryGradient}>
              <StyledFixedSubCategoryTabList>
                <SubCategoryTab tabTitle={TabTitleEnum.CategoryAll} isActive={!subCategoryId} />
                {subCategories.map((subCategory) => (
                  <SubCategoryTab
                    key={subCategory.gid}
                    tabTitle={subCategory.name}
                    categoryId={subCategory.gid ?? ''}
                    isActive={subCategoryId === subCategory.gid}
                  />
                ))}
              </StyledFixedSubCategoryTabList>
            </DragScrollWrapper>
            {visibleSubGradient.leftGradient && <StyledTabLeftGradient />}
            {visibleSubGradient.rightGradient && <StyledTabRightGradient />}
          </StyledFixedSubCategory>
        </>
      )}
    </>
  )
}

const rippleButtonCSS = css`
  @keyframes ripple {
    from {
      transform: scale(0.3);
      opacity: 1;
    }
    to {
      transform: scale(4);
      opacity: 1;
    }
  }

  span.ripple {
    z-index: 1;
    position: absolute;
    border-radius: ${({ theme: { spacing } }) => spacing.per50};
    transform: scale(0);
    animation: ripple 0.3s cubic-bezier(0.65, 0, 0.35, 1);
    background-color: ${({ theme: { color } }) => color.gray50};
  }

  p {
    position: relative;
    z-index: 2;
  }
`

const StyledRootCategory = styled.div`
  ${rippleButtonCSS};
  z-index: ${Z_INDEXES.ROOT_CATEGORY_NAVIGATION};
  top: 5.6rem;
  position: -webkit-sticky;
  position: sticky;
  background-color: ${({ theme: { color } }) => color.grayWhite};
  border-bottom: ${({ theme: { spacing } }) => spacing.size1} solid ${({ theme: { color } }) => color.gray70};
`

const StyledRootCategoryTabList = styled.div`
  padding-left: ${({ theme: { spacing } }) => spacing.size16};
  width: max-content;
  display: flex;
  justify-content: left;
  align-items: center;
`

const StyledTabLeftGradient = styled.div`
  z-index: 10;
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ theme: { spacing } }) => spacing.size32};
  height: ${({ theme: { spacing } }) => spacing.size46};
  background: ${({ theme: { color } }) =>
    `linear-gradient(-270deg, ${color.grayWhite} 0%, rgba(255, 255, 255, 0) 100%)`};
`

const StyledTabRightGradient = styled.div`
  z-index: 10;
  position: absolute;
  top: 0;
  right: 0;
  width: ${({ theme: { spacing } }) => spacing.size32};
  height: ${({ theme: { spacing } }) => spacing.size46};
  background: ${({ theme: { color } }) =>
    `linear-gradient(270deg, ${color.grayWhite} 0%, rgba(255, 255, 255, 0) 100%)`};
`

const StyledSubCategoryTabList = styled.div`
  ${rippleButtonCSS};
  position: relative;
  z-index: 3;
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: ${({ theme: { spacing } }) => spacing.size1};

  > a > div {
    outline: ${({ theme: { spacing } }) => spacing.size1} solid ${({ theme: { color } }) => color.gray50};
  }
`

const StyledFixedSubCategory = styled.div<{
  isFixedTabList: boolean
  isScrollDown: boolean
}>`
  z-index: ${Z_INDEXES.SUB_CATEGORY_NAVIGATION};
  top: 0;
  position: fixed;
  width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  opacity: ${({ isFixedTabList, isScrollDown }) => (!isScrollDown && isFixedTabList ? 1 : 0)};
  transform: ${({ isFixedTabList, isScrollDown }) => {
    if (!isScrollDown && isFixedTabList) {
      return `translate3d(0, 10.4rem, 0)`
    } else {
      return `translate3d(0, 0, 0)`
    }
  }};
  transition: transform 0.3s cubic-bezier(0.65, 0, 0.35, 1), opacity 0.2s cubic-bezier(0.65, 0, 0.35, 1);
  background-color: ${({ theme: { color } }) => color.grayWhite};
  border-bottom: ${({ theme: { spacing } }) => spacing.size1} solid ${({ theme: { color } }) => color.gray70};
`

const StyledFixedSubCategoryTabList = styled.div`
  min-width: max-content;
  display: flex;
  justify-content: left;
  align-items: center;
  background-color: ${({ theme: { color } }) => color.grayWhite};
`

const StyledDivider = styled.div`
  height: ${({ theme: { spacing } }) => spacing.size8};
  background-color: ${({ theme: { color } }) => color.gray70};
`

export default CategoryNavigation
